(() => {
  'use strict';

  if (customElements.get('fw-restart-notice')) return;
  const seenThisPage = new Set();

  class FeinwaelderRestartNotice extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.storageKey = this.dataset.storageKey || 'fw-restart-notice-2026-10-01-direct-purchase';
      this.designMode = this.dataset.designMode === 'true';
      this.autoOpen = this.dataset.pageType === 'index';
      this.reviewMode = new URLSearchParams(window.location.search).get('fw_notice');
      this.opener = this.querySelector('[data-notice-open]');
      this.nativeDialog = typeof this.dialog.showModal === 'function';

      if (!this.nativeDialog) {
        this.classList.add('fw-restart-notice--inline');
        this.dialog.setAttribute('role', 'region');
        this.dialog.removeAttribute('aria-modal');
      }

      this.querySelectorAll('[data-notice-close], [data-notice-primary]').forEach((button) => {
        button.addEventListener('click', () => this.closeNotice(), options);
      });
      this.opener?.addEventListener('click', () => this.openNotice(), options);

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeNotice();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (this.isOpen && !this.dialog.open) this.finishClose();
      }, options);
      this.dialog.addEventListener('keydown', (event) => this.trapTab(event), options);
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropPointer = event.button === 0 && this.isBackdrop(event) ? event.pointerId : null;
      }, options);
      this.dialog.addEventListener('pointerup', (event) => {
        const shouldClose = this.backdropPointer === event.pointerId && this.isBackdrop(event);
        this.backdropPointer = null;
        if (shouldClose) this.closeNotice();
      }, options);
      this.dialog.addEventListener('pointercancel', () => {
        this.backdropPointer = null;
      }, options);

      document.addEventListener('shopify:section:select', (event) => {
        if (this.designMode && this.reviewMode !== '0' && event.target.contains(this)) this.openNotice();
      }, options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.cleanup();
      }, options);

      if (this.reviewMode !== '0' && (this.reviewMode === '1' || (!this.designMode && this.autoOpen && !this.hasSeen()))) {
        this.setPlaybackState('pending');
        this.openTimer = window.setTimeout(() => this.openNotice(), 500);
      } else {
        this.setPlaybackState('closed');
      }
    }

    disconnectedCallback() {
      this.cleanup();
    }

    setPlaybackState(state) {
      if (this.dataset.playbackState === state) return;
      this.dataset.playbackState = state;
      this.dispatchEvent(new CustomEvent('fw:restart-notice-state', {
        bubbles: true,
        detail: { blocking: state === 'pending' || state === 'open' }
      }));
    }

    hasSeen() {
      if (seenThisPage.has(this.storageKey)) return true;
      try {
        return Boolean(window.sessionStorage.getItem(this.storageKey));
      } catch (_) {
        return false;
      }
    }

    remember(value) {
      if (this.designMode) return;
      seenThisPage.add(this.storageKey);
      try {
        window.sessionStorage.setItem(this.storageKey, value);
      } catch (_) {
        // The notice remains usable when a browser blocks session storage.
      }
    }

    openNotice() {
      window.clearTimeout(this.openTimer);
      if (!this.isConnected) {
        this.setPlaybackState('closed');
        return;
      }
      if (this.isOpen || this.dialog.open) {
        this.setPlaybackState('open');
        return;
      }
      const active = document.activeElement;
      this.returnFocus = active && active !== document.body && active !== document.documentElement
        ? active : this.opener;

      if (this.nativeDialog) {
        try {
          this.dialog.showModal();
        } catch (_) {
          this.setPlaybackState('closed');
          return;
        }
        this.lockScroll();
      } else {
        this.dialog.setAttribute('open', '');
      }

      this.isOpen = true;
      this.setPlaybackState('open');
      this.opener?.setAttribute('aria-expanded', 'true');
      this.remember('seen');
      this.querySelector('[data-notice-heading]')?.focus({ preventScroll: true });
    }

    closeNotice(remember = true) {
      window.clearTimeout(this.openTimer);
      if (!this.isOpen) {
        this.setPlaybackState('closed');
        return;
      }
      if (this.nativeDialog && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
      this.finishClose(remember);
    }

    finishClose(remember = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.setPlaybackState('closed');
      this.backdropPointer = null;
      this.opener?.setAttribute('aria-expanded', 'false');
      if (remember) this.remember('dismissed');
      this.unlockScroll();

      const canFocus = (element) => element?.isConnected && typeof element.focus === 'function'
        && !element.matches(':disabled') && element.getClientRects().length > 0;
      const target = canFocus(this.returnFocus) ? this.returnFocus : this.opener;
      if (canFocus(target)) target.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    isBackdrop(event) {
      if (!this.nativeDialog || event.target !== this.dialog) return false;
      const bounds = this.dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right
        || event.clientY < bounds.top || event.clientY > bounds.bottom;
    }

    trapTab(event) {
      if (!this.isOpen || !this.nativeDialog || event.key !== 'Tab'
        || event.altKey || event.ctrlKey || event.metaKey) return;

      const focusable = [...this.dialog.querySelectorAll('a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]')]
        .filter((element) => element.tabIndex >= 0 && !element.matches(':disabled')
          && !element.closest('[inert]') && element.getClientRects().length > 0
          && window.getComputedStyle(element).visibility !== 'hidden');
      const activeIndex = focusable.indexOf(document.activeElement);
      if (!focusable.length) {
        event.preventDefault();
        this.querySelector('[data-notice-heading]')?.focus({ preventScroll: true });
      } else if (activeIndex === -1 || (event.shiftKey && activeIndex === 0)
        || (!event.shiftKey && activeIndex === focusable.length - 1)) {
        event.preventDefault();
        focusable[event.shiftKey ? focusable.length - 1 : 0].focus({ preventScroll: true });
      }
    }

    lockScroll() {
      if (this.scrollLock) return;
      const x = window.scrollX;
      const y = window.scrollY;
      const saved = [];
      const setStyle = (element, property, value) => {
        saved.push([element, property, element.style.getPropertyValue(property), element.style.getPropertyPriority(property)]);
        element.style.setProperty(property, value, 'important');
      };
      this.scrollLock = { x, y, saved };
      setStyle(document.documentElement, 'overflow', 'hidden');
      setStyle(document.body, 'position', 'fixed');
      setStyle(document.body, 'top', `${-y}px`);
      setStyle(document.body, 'left', `${-x}px`);
      setStyle(document.body, 'width', '100%');
      setStyle(document.body, 'overflow', 'hidden');
    }

    unlockScroll() {
      if (!this.scrollLock) return;
      const { x, y, saved } = this.scrollLock;
      this.scrollLock = null;
      saved.reverse().forEach(([element, property, value, priority]) => {
        if (value) element.style.setProperty(property, value, priority);
        else element.style.removeProperty(property);
      });
      const rootStyle = document.documentElement.style;
      const behavior = rootStyle.getPropertyValue('scroll-behavior');
      const priority = rootStyle.getPropertyPriority('scroll-behavior');
      rootStyle.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(x, y);
      if (behavior) rootStyle.setProperty('scroll-behavior', behavior, priority);
      else rootStyle.removeProperty('scroll-behavior');
    }

    cleanup() {
      window.clearTimeout(this.openTimer);
      this.controller?.abort();
      this.controller = null;
      this.closeNotice(false);
      this.unlockScroll();
    }
  }

  customElements.define('fw-restart-notice', FeinwaelderRestartNotice);
})();
